/**
 * Add organization to events pure helpers (unit-tested in isolation from the network)
 */

import {
  paymentPlatformType,
  EXCLUSIVE_ORGANIZATION_COUNT,
  LOW_ORGANIZATION_COUNT,
  eventStatus
} from './constants';
import { systemSettingsRoutes } from '../constants';
import {
  BulkAddResult,
  Organization,
  OrganizationEvent,
  EventFilters
} from './types';

export const defaultFilters: EventFilters = {
  text: '',
  country: '',
  state: '',
  upcomingOnly: true,
  hideClosed: true,
  hideSingleOrganization: true,
  showAlreadyAdded: false
};

export const isExclusive = (event: OrganizationEvent): boolean =>
  event.organizationsCount === EXCLUSIVE_ORGANIZATION_COUNT;

export const isLowCount = (event: OrganizationEvent): boolean =>
  !isExclusive(event) && event.organizationsCount < LOW_ORGANIZATION_COUNT;

export const isClosed = (event: OrganizationEvent): boolean =>
  event.status === eventStatus.completed;

/** Local midnight at the start of the current day. */
export const startOfToday = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

/** Local midnight of the day the event starts, or null without a start date. */
const startDay = (event: OrganizationEvent): number | null => {
  if (!event.start) {
    return null;
  }
  const start = new Date(event.start);
  return new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate()
  ).getTime();
};

/** Starts today or later. An event without a start date is not upcoming. */
export const isUpcoming = (event: OrganizationEvent, today: Date): boolean => {
  const day = startDay(event);
  return day !== null && day >= today.getTime();
};

/**
 * Already added events are exempt from the upcoming and closed filters, and a
 * picked single organization event stays visible, so hiding a group never
 * hides a confirmed pick.
 */
export const filterEvents = (
  events: OrganizationEvent[],
  filters: EventFilters,
  selected: Set<number>,
  today: Date
): OrganizationEvent[] => {
  const text = filters.text.trim().toLowerCase();
  return events.filter(
    (event) =>
      (filters.showAlreadyAdded || !event.alreadyAdded) &&
      (!filters.upcomingOnly ||
        isUpcoming(event, today) ||
        event.alreadyAdded) &&
      (!filters.hideClosed || !isClosed(event) || event.alreadyAdded) &&
      (!filters.hideSingleOrganization ||
        !isExclusive(event) ||
        event.alreadyAdded ||
        selected.has(event.id)) &&
      (!filters.country || event.country === filters.country) &&
      (!filters.state || event.state === filters.state) &&
      (!text || event.name.toLowerCase().includes(text))
  );
};

const eventGroup = { upcoming: 0, past: 1, undated: 2 } as const;

const groupOf = (day: number | null, today: number): number => {
  if (day === null) {
    return eventGroup.undated;
  }
  return day >= today ? eventGroup.upcoming : eventGroup.past;
};

/**
 * Upcoming events soonest first, then past events most recent first, then
 * events without a start date. Events on the same day sort by name.
 */
export const sortEvents = (
  events: OrganizationEvent[],
  today: Date
): OrganizationEvent[] => {
  const todayTime = today.getTime();
  return [...events].sort((a, b) => {
    const dayA = startDay(a);
    const dayB = startDay(b);
    const groupA = groupOf(dayA, todayTime);
    const byGroup = groupA - groupOf(dayB, todayTime);
    if (byGroup !== 0) {
      return byGroup;
    }
    if (dayA !== null && dayB !== null && dayA !== dayB) {
      return groupA === eventGroup.upcoming ? dayA - dayB : dayB - dayA;
    }
    return a.name.localeCompare(b.name);
  });
};

/**
 * Ids the header checkbox selects: visible events that are not already added
 * and not single organization. Single organization events need an explicit, confirmed
 * selection each.
 */
export const bulkSelectableIds = (visible: OrganizationEvent[]): number[] =>
  visible
    .filter((event) => !event.alreadyAdded && !isExclusive(event))
    .map((event) => event.id);

export type HeaderCheckboxState = {
  checked: boolean;
  indeterminate: boolean;
};

/** Reflects the events select all picks, as returned by bulkSelectableIds. */
export const getHeaderCheckboxState = (
  visible: OrganizationEvent[],
  selected: Set<number>
): HeaderCheckboxState => {
  const bulk = bulkSelectableIds(visible);
  const checked = bulk.length > 0 && bulk.every((id) => selected.has(id));
  const some = bulk.some((id) => selected.has(id));
  return { checked, indeterminate: some && !checked };
};

/** Sorted distinct non-empty values, for the country and state dropdowns. */
export const distinctValues = (values: (string | null)[]): string[] =>
  Array.from(new Set(values.filter((value): value is string => !!value))).sort(
    (a, b) => a.localeCompare(b)
  );

export const formatLocation = (event: OrganizationEvent): string =>
  [event.city, event.state, event.country].filter(Boolean).join(', ');

export const formatOrganizationLocation = (
  organization: Organization
): string =>
  [organization.state, organization.country].filter(Boolean).join(', ');

export const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

export const emptyBulkAddResult = (): BulkAddResult => ({
  added: [],
  alreadyAdded: [],
  skipped: []
});

export const mergeBulkAddResults = (
  total: BulkAddResult,
  batch: BulkAddResult
): BulkAddResult => ({
  added: [...total.added, ...(batch.added ?? [])],
  alreadyAdded: [...total.alreadyAdded, ...(batch.alreadyAdded ?? [])],
  skipped: [...total.skipped, ...(batch.skipped ?? [])]
});

// Each step has its own URL, so a reload or the browser back button lands on it.
export const organizationEventsPath = (organizationId: number): string =>
  `${systemSettingsRoutes.ADD_ORGANIZATION_TO_EVENTS}/${organizationId}`;

/** A route id is only usable as a positive whole number. */
export const parseOrganizationId = (raw: string | undefined): number | null => {
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : null;
};

/** Router state the search step hands to step 2, so going back restores the results. */
export interface SearchReturnState {
  search: string;
}

export const searchReturnState = (
  params: URLSearchParams
): SearchReturnState => {
  const query = params.toString();
  return { search: query ? `?${query}` : '' };
};

/**
 * Where back goes from step 2: the search the user came from, or a fresh
 * search when the page was opened directly. History state is untyped, so it
 * is checked before use.
 */
export const searchPathFrom = (state: unknown): string => {
  const search = (state as Partial<SearchReturnState> | null)?.search;
  const query =
    typeof search === 'string' && search.startsWith('?') ? search : '';
  return `${systemSettingsRoutes.ADD_ORGANIZATION_TO_EVENTS}${query}`;
};

/**
 * Whether an organisation can be given event campaigns: a card payment
 * platform and not opted out of event campaign creation. Mirrors the check in
 * core's bulk add, so the Organisations grid only offers "Add to events" when
 * it can succeed.
 */
export const canAddToEvents = (organization: {
  eventCampaignCreate?: boolean | null;
  paymentPlatforms?: { platformType: string }[] | null;
}): boolean =>
  organization.eventCampaignCreate !== false &&
  (organization.paymentPlatforms ?? []).some(
    (platform) => platform.platformType === paymentPlatformType.creditCardForm
  );
